
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Camera, Zap, Video, Smartphone, Check, Sparkles, Users, RefreshCcw } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/ui/GlassCard';
import { PRODUCTS } from '../constants';
import { InquiryPayload } from '../types';

// Simple types for the wizard
type EventType = 'wedding' | 'corporate' | 'party' | 'brand';
type Vibe = 'fun' | 'glam' | 'branding' | 'memories';
type BudgetLevel = 'economy' | 'standard' | 'premium';

interface WizardState {
  step: number;
  eventType: EventType | null;
  vibe: Vibe | null;
  budget: BudgetLevel | null;
  addons: {
    photographer: boolean;
    audioGuestbook: boolean;
  };
}

export const EventWizard = ({ onOpenQuote }: { onOpenQuote: (data?: InquiryPayload) => void }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<WizardState>({
    step: 1,
    eventType: null,
    vibe: null,
    budget: null,
    addons: {
      photographer: false,
      audioGuestbook: false,
    }
  });

  // Effect to reset wizard if navigated with reset state
  useEffect(() => {
    if (location.state && (location.state as any).reset) {
        setState({
            step: 1,
            eventType: null,
            vibe: null,
            budget: null,
            addons: {
              photographer: false,
              audioGuestbook: false,
            }
        });
        // Clear history state to prevent loop on refresh/back (optional but good practice)
        window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Helper to move steps
  const nextStep = () => setState(prev => ({ ...prev, step: prev.step + 1 }));
  const prevStep = () => setState(prev => ({ ...prev, step: prev.step - 1 }));

  // Logic to determine recommendation
  const getRecommendation = () => {
    // Default fallback
    let primaryId = 'photostation';
    let secondaryId = 'roam';

    // 1. Corporate Logic
    if (state.eventType === 'corporate' || state.eventType === 'brand') {
      if (state.vibe === 'branding') primaryId = 'brandpix'; // AI Branding
      else if (state.vibe === 'fun') primaryId = '360-video';
      else primaryId = 'mosaix';
    } 
    // 2. Wedding Logic
    else if (state.eventType === 'wedding') {
      if (state.vibe === 'glam') primaryId = 'photostation'; // Glam booth
      else if (state.vibe === 'fun') primaryId = '360-video';
      else primaryId = 'audio-guest';
    }
    // 3. Party Logic
    else {
      if (state.budget === 'economy') primaryId = 'roam';
      else primaryId = '360-video';
    }

    // Secondary Logic based on Budget/Addons
    if (state.budget === 'premium' && primaryId !== '360-video') {
      secondaryId = '360-video';
    } else if (state.addons.photographer) {
      secondaryId = 'roam'; // Roamer acts as photographer
    } else {
      secondaryId = primaryId === 'brandpix' ? 'photostation' : 'brandpix';
    }

    const primaryProduct = PRODUCTS.find(p => p.id === primaryId);
    const secondaryProduct = PRODUCTS.find(p => p.id === secondaryId);

    return { primaryProduct, secondaryProduct };
  };

  // Logic to calculate estimated price
  const getEstimatedPrice = () => {
    let base = 0;
    if (state.budget === 'economy') base = 800;
    if (state.budget === 'standard') base = 1500;
    if (state.budget === 'premium') base = 2500;

    if (state.addons.photographer) base += 600;
    if (state.addons.audioGuestbook) base += 350;

    return { min: base, max: base + 500 };
  };

  const { primaryProduct, secondaryProduct } = getRecommendation();
  const price = getEstimatedPrice();

  const handleBookPackage = () => {
      const services = [];
      if (primaryProduct) services.push(primaryProduct.name);
      if (state.addons.photographer) services.push("Roaming Photographer");
      if (state.addons.audioGuestbook) services.push("Audio Guestbook");
      if (secondaryProduct && !state.addons.photographer) services.push(secondaryProduct.name); // Secondary usually included unless photog overrides

      const packageDescription = `${state.budget?.toUpperCase()} TIER: ${primaryProduct?.name} + ${secondaryProduct?.name || 'Add-on'}`;

      // Mapping event types to form values
      const eventTypeMap: Record<string, string> = {
          wedding: 'Wedding',
          corporate: 'Corporate Event',
          brand: 'Brand Activation',
          party: 'Private Party'
      };

      onOpenQuote({
          eventType: eventTypeMap[state.eventType || 'party'],
          services: services,
          packageDetails: packageDescription
      });
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 relative overflow-hidden">
      {/* Background Ambience */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute top-[10%] left-[20%] w-[600px] h-[600px] bg-brand-neonBlue/5 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-[10%] right-[20%] w-[600px] h-[600px] bg-brand-neonPink/5 rounded-full blur-[100px]"></div>
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-brand-neonBlue font-bold tracking-widest text-xs uppercase mb-2 block animate-pulse">Event Recommendation Engine</span>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">Find Your Perfect Match</h1>
          <p className="text-slate-400 max-w-xl mx-auto">
            Answer 4 simple questions and our AI will bundle the perfect photo experience for your event style and budget.
          </p>
        </div>

        {/* Wizard Container */}
        <GlassCard className="min-h-[500px] flex flex-col relative border-brand-neonBlue/20 shadow-[0_0_50px_rgba(15,23,42,0.5)]">
          
          {/* Progress Bar */}
          <div className="absolute top-0 left-0 w-full h-1 bg-slate-800">
            <div 
              className="h-full bg-gradient-to-r from-brand-neonBlue to-brand-neonPink transition-all duration-500 ease-out"
              style={{ width: `${(state.step / 5) * 100}%` }}
            ></div>
          </div>

          {/* STEP 1: Event Type */}
          {state.step === 1 && (
            <div className="flex-1 flex flex-col animate-fade-in-up">
              <h2 className="text-2xl font-bold text-white mb-8 text-center">What type of event are you planning?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { id: 'wedding', label: 'Wedding / Engagement', icon: <Sparkles className="text-brand-neonPink" /> },
                  { id: 'corporate', label: 'Corporate Function', icon: <Users className="text-brand-neonBlue" /> },
                  { id: 'brand', label: 'Brand Activation', icon: <Zap className="text-yellow-400" /> },
                  { id: 'party', label: 'Private Party / Birthday', icon: <Video className="text-purple-400" /> },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => {
                      setState(prev => ({ ...prev, eventType: opt.id as EventType }));
                      nextStep();
                    }}
                    className="group p-6 rounded-xl border border-white/10 bg-slate-900/50 hover:bg-white/5 hover:border-brand-neonBlue transition-all text-left flex items-center gap-4"
                  >
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                      {opt.icon}
                    </div>
                    <span className="text-lg font-medium text-white">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: Vibe */}
          {state.step === 2 && (
            <div className="flex-1 flex flex-col animate-fade-in-up">
              <h2 className="text-2xl font-bold text-white mb-8 text-center">What is the main goal or vibe?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { id: 'fun', label: 'High Energy & Fun', desc: 'Focus on dancing and movement' },
                  { id: 'glam', label: 'Classy & Glamorous', desc: 'Kardashian-style beauty filter' },
                  { id: 'branding', label: 'Brand Awareness', desc: 'Logos, data capture, social sharing' },
                  { id: 'memories', label: 'Sentimental Memories', desc: 'Audio messages and prints' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => {
                      setState(prev => ({ ...prev, vibe: opt.id as Vibe }));
                      nextStep();
                    }}
                    className="group p-6 rounded-xl border border-white/10 bg-slate-900/50 hover:bg-white/5 hover:border-brand-neonPink transition-all text-left"
                  >
                    <span className="text-lg font-bold text-white block mb-1">{opt.label}</span>
                    <span className="text-sm text-slate-400">{opt.desc}</span>
                  </button>
                ))}
              </div>
              <div className="mt-auto pt-8">
                <Button variant="outline" onClick={prevStep} size="sm">Back</Button>
              </div>
            </div>
          )}

          {/* STEP 3: Budget */}
          {state.step === 3 && (
            <div className="flex-1 flex flex-col animate-fade-in-up">
              <h2 className="text-2xl font-bold text-white mb-8 text-center">What is your approximate budget?</h2>
              <div className="space-y-4 max-w-md mx-auto w-full">
                {[
                  { id: 'economy', label: 'Economy', range: '$800 - $1,200', desc: 'Great value, core features.' },
                  { id: 'standard', label: 'Standard', range: '$1,500 - $2,200', desc: 'Most popular. Premium lighting & props.' },
                  { id: 'premium', label: 'Premium / Unlimited', range: '$2,500+', desc: 'The full celebrity experience.' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => {
                      setState(prev => ({ ...prev, budget: opt.id as BudgetLevel }));
                      nextStep();
                    }}
                    className="w-full group p-4 rounded-xl border border-white/10 bg-slate-900/50 hover:bg-white/5 hover:border-brand-neonBlue transition-all flex justify-between items-center"
                  >
                    <div className="text-left">
                      <span className="text-lg font-bold text-white block">{opt.label}</span>
                      <span className="text-xs text-slate-400">{opt.desc}</span>
                    </div>
                    <span className="text-brand-neonBlue font-display font-bold">{opt.range}</span>
                  </button>
                ))}
              </div>
              <div className="mt-auto pt-8">
                <Button variant="outline" onClick={prevStep} size="sm">Back</Button>
              </div>
            </div>
          )}

          {/* STEP 4: Add-ons (Bundling) */}
          {state.step === 4 && (
            <div className="flex-1 flex flex-col animate-fade-in-up">
              <h2 className="text-2xl font-bold text-white mb-2 text-center">Bundle & Save</h2>
              <p className="text-slate-400 text-center mb-8">Add these services to save 15% on your total package.</p>
              
              <div className="grid grid-cols-1 gap-4 max-w-lg mx-auto w-full mb-8">
                {/* Photographer Option */}
                <div 
                  onClick={() => setState(prev => ({ ...prev, addons: { ...prev.addons, photographer: !prev.addons.photographer } }))}
                  className={`cursor-pointer p-6 rounded-xl border transition-all flex items-center justify-between ${
                    state.addons.photographer ? 'bg-brand-neonBlue/10 border-brand-neonBlue' : 'bg-slate-900/50 border-white/10'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white">
                      <Camera size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white">Roaming Photographer</h3>
                      <p className="text-xs text-slate-400">Candid shots of guests + digital delivery.</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center ${
                    state.addons.photographer ? 'bg-brand-neonBlue border-brand-neonBlue' : 'border-slate-600'
                  }`}>
                    {state.addons.photographer && <Check size={14} className="text-black" />}
                  </div>
                </div>

                {/* Audio Guestbook Option */}
                <div 
                  onClick={() => setState(prev => ({ ...prev, addons: { ...prev.addons, audioGuestbook: !prev.addons.audioGuestbook } }))}
                  className={`cursor-pointer p-6 rounded-xl border transition-all flex items-center justify-between ${
                    state.addons.audioGuestbook ? 'bg-brand-neonBlue/10 border-brand-neonBlue' : 'bg-slate-900/50 border-white/10'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white">
                      <Smartphone size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white">Audio Guestbook</h3>
                      <p className="text-xs text-slate-400">Vintage phone for voicemail messages.</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center ${
                    state.addons.audioGuestbook ? 'bg-brand-neonBlue border-brand-neonBlue' : 'border-slate-600'
                  }`}>
                    {state.addons.audioGuestbook && <Check size={14} className="text-black" />}
                  </div>
                </div>
              </div>

              <div className="mt-auto pt-8 flex justify-between">
                <Button variant="outline" onClick={prevStep} size="sm">Back</Button>
                <Button onClick={nextStep}>See My Results</Button>
              </div>
            </div>
          )}

          {/* STEP 5: Results */}
          {state.step === 5 && primaryProduct && (
            <div className="flex-1 flex flex-col animate-fade-in-up">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-bold uppercase tracking-wider mb-2">
                  <Check size={12} /> Perfect Match Found
                </div>
                <h2 className="text-3xl font-display font-bold text-white">Your Curated Package</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Primary Recommendation */}
                <div className="relative group rounded-xl overflow-hidden border border-brand-neonBlue bg-slate-900/80">
                  <div className="absolute top-0 left-0 w-full h-1 bg-brand-neonBlue"></div>
                  <div className="p-1 bg-brand-neonBlue absolute top-3 right-3 text-[10px] font-bold text-black uppercase px-2 rounded">
                    Best For {state.eventType}
                  </div>
                  
                  <div className="h-32 overflow-hidden">
                    <img src={primaryProduct.image} alt={primaryProduct.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-xl font-bold text-white mb-1">{primaryProduct.name}</h3>
                    <p className="text-xs text-slate-400 mb-3">{primaryProduct.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {primaryProduct.features.slice(0, 2).map((f, i) => (
                        <span key={i} className="text-[10px] bg-white/5 border border-white/10 px-2 py-0.5 rounded text-slate-300">{f}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bundle Items / Secondary */}
                <div className="space-y-3">
                  <div className="p-4 rounded-xl border border-white/10 bg-slate-900/50 flex items-center gap-4">
                    <div className="w-12 h-12 rounded bg-slate-800 shrink-0 overflow-hidden">
                         {state.addons.photographer ? 
                            <div className="w-full h-full flex items-center justify-center"><Camera size={20} className="text-white"/></div> :
                            <img src={secondaryProduct?.image} className="w-full h-full object-cover"/>
                         }
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-sm">
                        {state.addons.photographer ? 'Roaming Photographer' : (secondaryProduct?.name || 'Digital Booth')}
                      </h4>
                      <p className="text-xs text-slate-400">Included in bundle</p>
                    </div>
                  </div>

                  {state.addons.audioGuestbook && (
                    <div className="p-4 rounded-xl border border-white/10 bg-slate-900/50 flex items-center gap-4">
                      <div className="w-12 h-12 rounded bg-slate-800 shrink-0 flex items-center justify-center">
                        <Smartphone size={20} className="text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white text-sm">Audio Guestbook</h4>
                        <p className="text-xs text-slate-400">Vintage Phone Setup</p>
                      </div>
                    </div>
                  )}

                  {/* Price Estimate */}
                  <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-brand-neonBlue/10 to-brand-neonPink/10 border border-white/10 text-center">
                    <p className="text-xs text-slate-400 uppercase tracking-widest mb-1">Estimated Package Price</p>
                    <div className="text-2xl font-display font-bold text-white">
                      ${price.min} - ${price.max}
                    </div>
                    {Object.values(state.addons).some(v => v) && (
                      <span className="text-[10px] text-brand-neonPink font-bold uppercase mt-1 block">Includes Bundle Discount</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-auto flex flex-col gap-3">
                <Button size="lg" onClick={handleBookPackage} className="w-full">
                  Request Firm Proposal
                </Button>
                <button 
                  onClick={() => setState({ step: 1, eventType: null, vibe: null, budget: null, addons: { photographer: false, audioGuestbook: false } })}
                  className="flex items-center justify-center gap-2 text-slate-400 hover:text-white text-sm py-2"
                >
                  <RefreshCcw size={14} /> Start Over
                </button>
              </div>
            </div>
          )}

        </GlassCard>
      </div>
    </div>
  );
};
